// Challenge signing and verification (ECDSA P-256, IEEE-P1363).
//
// This module turns a `(nonce, timestamp, sessionId)` triple into the
// canonical 56-byte challenge bytes that both the Web_Client and the
// API_Server agree on, signs them with the user's Identity_Key, and
// verifies signatures on the server side. It is the mechanism that
// backs every authenticated session establishment (signup, login,
// fresh-signature for identity-bearing mutations) called out in
// Requirements 2.2 and 2.3.
//
// Canonical challenge layout (56 bytes total):
//
//     offset  0 .. 31   nonce          32 bytes  (CSPRNG, server-issued)
//     offset 32 .. 39   timestamp      8 bytes   (uint64 big-endian, ms since epoch)
//     offset 40 .. 55   session_id     16 bytes  (server-issued opaque ID)
//
// The timestamp is encoded in milliseconds since the Unix epoch to
// match the `server_time_ms` field returned by `/v1/auth/*-challenge`
// and to give millisecond resolution to the 60-second freshness window
// the server enforces (Req 2.5; the freshness check itself lives in
// `apps/api/src/crypto/sigverify.ts`, task 6.2). 8 bytes / uint64
// cleanly handles dates well past year 2^64-1 ms ≈ year 584942417355.
//
// The signature is produced with Web Crypto's `subtle.sign` over the
// 56-byte buffer using `{ name: 'ECDSA', hash: 'SHA-256' }`. Web Crypto
// emits ECDSA signatures in IEEE-P1363 form (raw `r || s`, 64 bytes
// for P-256) — this is what the design document specifies and what
// the server-side verifier expects, so no DER conversion is needed at
// either end.
//
// `verifySignature` accepts the public key as either an already-imported
// `CryptoKey` or as the 65-byte uncompressed SEC1 encoding (the form
// stored in `accounts.public_key`). Malformed inputs throw a typed
// `CryptoInputError`; the cryptographic verify outcome is returned as
// a boolean. This separation is important: a thrown error means the
// caller passed something the function could not even attempt to
// verify, while `false` means the signature was checked and rejected.
// Conflating those two states has historically been a source of auth
// bypass bugs.
//
// _Requirements: 2.2, 2.3_

// ---------------------------------------------------------------------------
// Constants — challenge byte layout
// ---------------------------------------------------------------------------

/** Length of the server-issued random nonce in the challenge. */
export const CHALLENGE_NONCE_BYTES = 32;

/** Length of the big-endian uint64 timestamp field in the challenge. */
export const CHALLENGE_TIMESTAMP_BYTES = 8;

/** Length of the opaque server-issued session ID in the challenge. */
export const CHALLENGE_SESSION_ID_BYTES = 16;

/**
 * Total length of the canonical challenge buffer. Anyone reconstructing
 * the challenge for verification MUST produce exactly these bytes,
 * byte-for-byte; any framing tweak (e.g. a domain-separation prefix)
 * is a wire-protocol break.
 */
export const CHALLENGE_TOTAL_BYTES =
  CHALLENGE_NONCE_BYTES +
  CHALLENGE_TIMESTAMP_BYTES +
  CHALLENGE_SESSION_ID_BYTES; // 56

/** Length of an IEEE-P1363 ECDSA P-256 signature: r (32 bytes) || s (32 bytes). */
export const SIGNATURE_BYTES = 64;

/** Length of an uncompressed SEC1 P-256 public key. */
const SEC1_UNCOMPRESSED_LENGTH = 65;

/** First byte of an uncompressed SEC1 point. */
const SEC1_UNCOMPRESSED_TAG = 0x04;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The three server-issued inputs that go into the canonical challenge
 * bytes. Both `signChallenge` and `verifySignature` take this same
 * shape so callers cannot accidentally mix up which fields are which
 * — and so that the client and server reconstruct the exact same
 * 56-byte buffer.
 *
 * Field shapes:
 * - `nonce`     : exactly {@link CHALLENGE_NONCE_BYTES} bytes.
 * - `timestamp` : milliseconds since the Unix epoch, as either a
 *                 `bigint` (preferred for max precision) or a `Date`
 *                 (which is converted via `getTime()`). Must be
 *                 non-negative and fit in an unsigned 64-bit integer.
 * - `sessionId` : exactly {@link CHALLENGE_SESSION_ID_BYTES} bytes.
 */
export interface ChallengeInput {
  nonce: Uint8Array;
  timestamp: bigint | Date;
  sessionId: Uint8Array;
}

/**
 * Discriminated kinds of input-validation failure surfaced by
 * `signChallenge` / `verifySignature` / `buildChallengeBytes`.
 *
 * - `nonce_length`        : `nonce` was not exactly 32 bytes.
 * - `session_id_length`   : `sessionId` was not exactly 16 bytes.
 * - `timestamp_type`      : `timestamp` was neither a `bigint` nor a `Date`.
 * - `timestamp_range`     : `timestamp` was negative or did not fit in uint64.
 * - `signature_length`    : signature was not exactly 64 bytes.
 * - `public_key_length`   : raw public key was not exactly 65 bytes.
 * - `public_key_tag`      : raw public key did not begin with 0x04.
 * - `public_key_import`   : Web Crypto rejected the public-key import
 *                            (e.g. point not on the curve).
 */
export type CryptoInputErrorKind =
  | "nonce_length"
  | "session_id_length"
  | "timestamp_type"
  | "timestamp_range"
  | "signature_length"
  | "public_key_length"
  | "public_key_tag"
  | "public_key_import";

/**
 * Typed error thrown by this module when a caller passes input the
 * function cannot even attempt to sign or verify. Distinct from a
 * `verifySignature` returning `false` — that means the signature was
 * cryptographically checked and rejected; this means we never got
 * that far.
 */
export class CryptoInputError extends Error {
  constructor(
    public readonly kind: CryptoInputErrorKind,
    message: string,
    cause?: unknown,
  ) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = "CryptoInputError";
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build the canonical 56-byte challenge buffer from its three parts.
 * Exported because the property test in task 3.6 needs to assert
 * round-trip equality, and because callers that want to log or hash
 * the challenge bytes should use the same construction the signer
 * does.
 *
 * @throws {@link CryptoInputError} on malformed input.
 * _Requirements: 2.2, 2.3_
 */
export function buildChallengeBytes(input: ChallengeInput): Uint8Array {
  if (input.nonce.length !== CHALLENGE_NONCE_BYTES) {
    throw new CryptoInputError(
      "nonce_length",
      `nonce must be exactly ${CHALLENGE_NONCE_BYTES} bytes, got ${input.nonce.length}`,
    );
  }
  if (input.sessionId.length !== CHALLENGE_SESSION_ID_BYTES) {
    throw new CryptoInputError(
      "session_id_length",
      `sessionId must be exactly ${CHALLENGE_SESSION_ID_BYTES} bytes, got ${input.sessionId.length}`,
    );
  }
  const timestampBytes = packTimestampBe8(input.timestamp);

  const out = new Uint8Array(CHALLENGE_TOTAL_BYTES);
  out.set(input.nonce, 0);
  out.set(timestampBytes, CHALLENGE_NONCE_BYTES);
  out.set(
    input.sessionId,
    CHALLENGE_NONCE_BYTES + CHALLENGE_TIMESTAMP_BYTES,
  );
  return out;
}

/**
 * Sign the canonical challenge bytes with an ECDSA P-256 private key.
 *
 * Returns a 64-byte buffer in IEEE-P1363 (`r || s`) form — the same
 * shape Web Crypto emits natively and the same shape the server-side
 * verifier (`apps/api/src/crypto/sigverify.ts`, task 6.2) consumes.
 * No DER wrapping happens at any point.
 *
 * @throws {@link CryptoInputError} on malformed challenge input.
 * @throws if Web Crypto's `subtle.sign` rejects the call (e.g. the
 *         provided key is not an ECDSA P-256 private key with `sign`
 *         usage).
 * _Requirements: 2.2_
 */
export async function signChallenge(
  privateKey: CryptoKey,
  input: ChallengeInput,
): Promise<Uint8Array> {
  const subtle = getSubtle();
  const challenge = buildChallengeBytes(input);
  const signature = await subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    privateKey,
    challenge,
  );
  return new Uint8Array(signature);
}

/**
 * Verify a 64-byte IEEE-P1363 ECDSA P-256 signature over the canonical
 * challenge bytes.
 *
 * The public key may be passed as an already-imported `CryptoKey`
 * (the form held by the Web_Client after `deriveIdentityKey`) or as
 * the 65-byte uncompressed SEC1 encoding (the form stored in
 * `accounts.public_key` and the form a server sees on the wire). The
 * raw form is imported on-the-fly with `extractable: false`.
 *
 * @returns `true` iff the signature is valid for the given key and
 *          challenge; `false` iff Web Crypto's `subtle.verify` rejected
 *          it.
 * @throws {@link CryptoInputError} when an input is malformed and the
 *          signature could not even be checked. Callers MUST distinguish
 *          this from a `false` return — only `false` is "verified and
 *          rejected".
 * _Requirements: 2.3_
 */
export async function verifySignature(
  publicKey: CryptoKey | Uint8Array,
  input: ChallengeInput,
  signature: Uint8Array,
): Promise<boolean> {
  if (signature.length !== SIGNATURE_BYTES) {
    throw new CryptoInputError(
      "signature_length",
      `signature must be exactly ${SIGNATURE_BYTES} bytes, got ${signature.length}`,
    );
  }
  const challenge = buildChallengeBytes(input);
  const subtle = getSubtle();

  const key =
    publicKey instanceof Uint8Array
      ? await importRawP256PublicKey(publicKey)
      : publicKey;

  return subtle.verify(
    { name: "ECDSA", hash: "SHA-256" },
    key,
    signature,
    challenge,
  );
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      "Web Crypto subtle is not available in this runtime. " +
        "signChallenge/verifySignature require a Web Crypto-capable " +
        "environment (modern browsers, Node 20+, or Bun).",
    );
  }
  return subtle;
}

/**
 * Pack a `bigint | Date` timestamp (in milliseconds since the Unix
 * epoch) as 8 big-endian bytes (uint64).
 *
 * Rejecting non-bigint / non-Date inputs and out-of-range values up
 * front means the resulting challenge bytes are unambiguously a 64-bit
 * number; no caller has to worry about JS `number` precision (which
 * tops out at 53 bits) or about negative wall-clock times.
 */
function packTimestampBe8(timestamp: bigint | Date): Uint8Array {
  let ms: bigint;
  if (timestamp instanceof Date) {
    const t = timestamp.getTime();
    if (!Number.isFinite(t)) {
      throw new CryptoInputError(
        "timestamp_range",
        "timestamp Date is invalid (NaN getTime)",
      );
    }
    ms = BigInt(t);
  } else if (typeof timestamp === "bigint") {
    ms = timestamp;
  } else {
    throw new CryptoInputError(
      "timestamp_type",
      `timestamp must be a bigint or Date, got ${typeof timestamp}`,
    );
  }
  if (ms < 0n) {
    throw new CryptoInputError(
      "timestamp_range",
      `timestamp must be non-negative, got ${ms}`,
    );
  }
  if (ms >= 1n << 64n) {
    throw new CryptoInputError(
      "timestamp_range",
      `timestamp does not fit in uint64, got ${ms}`,
    );
  }

  const out = new Uint8Array(CHALLENGE_TIMESTAMP_BYTES);
  let v = ms;
  for (let i = CHALLENGE_TIMESTAMP_BYTES - 1; i >= 0; i--) {
    out[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return out;
}

/**
 * Import a 65-byte uncompressed SEC1 P-256 public key as a Web Crypto
 * `CryptoKey` with `verify` usage. Distinguishes between length / tag
 * issues (typed errors thrown synchronously after the check) and
 * Web-Crypto-level rejection (point not on curve, etc.) which becomes
 * a `public_key_import` `CryptoInputError` carrying the original cause.
 */
async function importRawP256PublicKey(raw: Uint8Array): Promise<CryptoKey> {
  if (raw.length !== SEC1_UNCOMPRESSED_LENGTH) {
    throw new CryptoInputError(
      "public_key_length",
      `raw public key must be exactly ${SEC1_UNCOMPRESSED_LENGTH} bytes, got ${raw.length}`,
    );
  }
  if (raw[0] !== SEC1_UNCOMPRESSED_TAG) {
    throw new CryptoInputError(
      "public_key_tag",
      `raw public key must start with the SEC1 uncompressed tag 0x04, got 0x${raw[0]!.toString(16)}`,
    );
  }
  try {
    return await getSubtle().importKey(
      "raw",
      // Pass a copy so Web Crypto doesn't alias the caller's buffer.
      raw.slice(),
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
  } catch (cause) {
    throw new CryptoInputError(
      "public_key_import",
      "Web Crypto rejected the raw P-256 public key import",
      cause,
    );
  }
}
